import { FormEvent, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { voidPayment, type PaymentRecord, type VoidPaymentInput } from '@/lib/api/payments.js';
import { parseApiError } from '@/lib/api/errors.js';
import { formatCurrency } from '@/lib/format.js';
import { paymentMethodLabel } from '@/lib/payment-display.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

export function VoidPaymentModal({
  open,
  payment,
  ticketNumber,
  customerName,
  managerOverrideAuthorizedBy,
  defaultTicketAction = 'reopen',
  onClose,
  onSuccess,
}: {
  open: boolean;
  payment: PaymentRecord | null;
  ticketNumber?: string;
  customerName?: string;
  managerOverrideAuthorizedBy: string;
  /** POS sales should usually keep closed with outstanding balance */
  defaultTicketAction?: 'reopen' | 'keep_closed';
  onClose: () => void;
  onSuccess: (result: Awaited<ReturnType<typeof voidPayment>>) => void;
}) {
  const [reason, setReason] = useState('');
  const [ticketAction, setTicketAction] = useState<'reopen' | 'keep_closed'>(defaultTicketAction);

  useEffect(() => {
    if (open) {
      setReason('');
      setTicketAction(defaultTicketAction);
    }
  }, [open, defaultTicketAction]);

  const mutation = useMutation({
    mutationFn: (body: VoidPaymentInput) => voidPayment(payment!.id, body),
    onSuccess: (result) => {
      const balance = result.ticket?.balanceDue ?? payment?.amount;
      if (ticketAction === 'reopen') {
        toastSuccess(
          `Payment voided — ${result.ticket?.ticketNumber ?? ticketNumber ?? 'ticket'} reopened`,
        );
      } else {
        toastSuccess(`Payment voided — balance due: ${formatCurrency(balance)}`);
      }
      onSuccess(result);
      onClose();
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  if (!open || !payment) return null;

  const balanceAfter = formatCurrency(payment.amount);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!reason.trim()) {
      toastError('Reason is required');
      return;
    }
    mutation.mutate({
      reason: reason.trim(),
      ticketAction,
      managerOverrideAuthorizedBy,
    });
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <button type="button" aria-label="Close" className="absolute inset-0 bg-slate-950/40" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl">
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-slate-950">Void Payment</h2>
          <button type="button" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" onClick={onClose}>
            <X className="h-5 w-5" />
          </button>
        </header>

        <form className="space-y-4 p-5" onSubmit={handleSubmit}>
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            <p className="font-semibold text-slate-950">
              Voiding: {paymentMethodLabel(payment.method)} · {formatCurrency(payment.amount)}
            </p>
            <p className="mt-1 text-slate-500">
              {[ticketNumber, customerName].filter(Boolean).join(' · ')}
            </p>
          </div>

          <label className="block space-y-1.5">
            <span className="text-sm font-semibold text-slate-700">Reason *</span>
            <textarea
              className="min-h-24 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder='e.g. "Entered wrong amount", "Customer paid different method"'
              required
            />
          </label>

          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              After voiding this payment, balance due will be: <strong>{balanceAfter}</strong>
            </p>
            {defaultTicketAction === 'keep_closed' && ticketNumber?.startsWith('SALE-') ? (
              <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                This POS sale will stay closed with an outstanding balance. Process a new payment or
                return from Sales History if needed.
              </p>
            ) : (
              <>
                <p className="text-sm font-semibold text-slate-800">What should happen to this ticket?</p>

                <label className="flex cursor-pointer gap-3 rounded-xl border border-slate-200 p-3 hover:bg-slate-50">
                  <input
                    type="radio"
                    name="ticketAction"
                    className="mt-1"
                    checked={ticketAction === 'reopen'}
                    onChange={() => setTicketAction('reopen')}
                  />
                  <span>
                    <span className="block text-sm font-semibold text-slate-950">
                      Reopen ticket (status → Ready)
                    </span>
                    <span className="text-xs text-slate-500">Customer still needs to pay</span>
                  </span>
                </label>

                <label className="flex cursor-pointer gap-3 rounded-xl border border-slate-200 p-3 hover:bg-slate-50">
                  <input
                    type="radio"
                    name="ticketAction"
                    className="mt-1"
                    checked={ticketAction === 'keep_closed'}
                    onChange={() => setTicketAction('keep_closed')}
                  />
                  <span>
                    <span className="block text-sm font-semibold text-slate-950">
                      Keep closed with outstanding balance
                    </span>
                    <span className="text-xs text-slate-500">Record payment separately</span>
                  </span>
                </label>
              </>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-slate-200 pt-4">
            <button
              type="button"
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
            >
              {mutation.isPending ? 'Voiding…' : 'Void Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
